import * as PIXI from "pixi.js";
import { memo } from "preact/compat";
import { useEffect, useRef, useState } from "preact/hooks";
import { useTranslation } from "react-i18next";
import { CountdownCircle } from "../../../components/CountdownCircle/CountdownCircle";
import { UserCashOutContainer } from "../../../components/UserCashOut";
import { useNotification } from "../../../shared/contexts";
import { GameState, GameStateEnum } from "../../../shared/types/global.types";
import { NotificationContainer } from "../../../shared/ui/Notification";
import useCamera from "../hooks/useCamera";
import { useSpineBackground } from "../hooks/useSpineBackground";
import { useSpineCapitalist } from "../hooks/useSpineCapitalist";
import { useSpineCharacter } from "../hooks/useSpineCharacter";
import { useSpineSpace } from "../hooks/useSpineSpace";
import { useSpineStairs } from "../hooks/useSpineStairs";
import css from "./PioneerGame.module.scss";

interface Props {
  animationName?: string;
  skinName?: string;
  multiplier?: number;
  gameState?: GameState;
  canvasWidth: number;
  canvasHeight: number;
  countdown?: number;
  countdownTotal?: number;
  onAnimationComplete?: (animationName: string) => void;
}
const InlineNotifications = () => {
  const { notifications, removeNotification } = useNotification();
  return (
    <NotificationContainer
      variant="inline"
      notifications={notifications}
      onRemove={removeNotification}
    />
  );
};

export const PioneerGame = memo(function PioneerGame({
  skinName,
  countdown,
  countdownTotal,
  multiplier = 1,
  onAnimationComplete,
  gameState,
  canvasWidth,
  canvasHeight,
}: Props) {
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const appRef = useRef<PIXI.Application | null>(null);
  const cameraRef = useRef<PIXI.Container | null>(null);
  const isCountdown = gameState === GameStateEnum.COUNTDOWN;

  const resizeTimeoutRef = useRef<number | null>(null);
  const pendingResizeRef = useRef<{ w: number; h: number } | null>(null);

  const backgroundHook = useSpineBackground({
    multiplier,
    gameState,
  });

  const spaceHook = useSpineSpace({
    multiplier,
    gameState,
  });

  const stairsHook = useSpineStairs({
    gameState: gameState,
  });
  const capitalistHook = useSpineCapitalist({
    gameState: gameState,
  });

  const characterHook = useSpineCharacter({
    skinName,
    onAnimationComplete,
    gameState,
  });

  const cameraApi = useCamera({
    currentAnim: characterHook.currentAnim,
    cameraRef,
    canvasWidth,
    gameState,
  });

  useEffect(() => {
    if (!canvasRef.current) return;

    let cancelled = false;

    const getSize = () => {
      return { w: canvasWidth, h: canvasHeight };
    };

    const init = async () => {
      try {
        const { w, h } = getSize();

        const app = new PIXI.Application();
        const dpr = window.devicePixelRatio || 1;

        const initOptions: any = {
          canvas: canvasRef.current!,
          width: w,
          height: h,
          backgroundColor: 0x0,
          antialias: true,
          resolution: Math.max(dpr, 2),
          preference: "webgl",
          powerPreference: "high-performance",
          roundPixels: false,
        };

        await app.init(initOptions);

        app.ticker.maxFPS = 60;
        app.ticker.minFPS = 30;
        app.ticker.start();

        if (cancelled) {
          app.destroy(true);
          return;
        }
        appRef.current = app;
        const camera = new PIXI.Container();
        app.stage.addChild(camera);
        cameraRef.current = camera;

        camera.scale.set(cameraApi.current.zoom);
        camera.position.set(cameraApi.current.x, cameraApi.current.y);

        setError(null);

        await spaceHook.createSpace(camera, w, h);
        await backgroundHook.createBackground(camera, w, h);
        await stairsHook.createStairs(camera, w, h);
        await characterHook.createCharacter(camera, w, h);
        await capitalistHook.createCapitalist(camera, w, h);

        if (characterHook.spineRef?.current) {
          camera.setChildIndex(
            characterHook.spineRef.current,
            camera.children.length - 1
          );
        }
      } catch (e: any) {
        console.error("Ошибка инициализации:", e);
        setError(String(e?.message || e));
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [skinName]);

  useEffect(() => {
    const app = appRef.current;
    if (!app || !canvasWidth || !canvasHeight) return;

    pendingResizeRef.current = { w: canvasWidth, h: canvasHeight };
    if (resizeTimeoutRef.current !== null) {
      clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = null;
    }

    resizeTimeoutRef.current = window.setTimeout(() => {
      const pending = pendingResizeRef.current;
      if (!pending) return;

      spaceHook.resizeSpace(pending.w, pending.h);
      backgroundHook.resizeBackground(pending.w, pending.h);
      stairsHook.resizeStairs(pending.w, pending.h);
      capitalistHook.resizeCapitalist(pending.w, pending.h);
      characterHook.resizeCharacter(pending.w, pending.h);

      app.renderer.resize(pending.w, pending.h);
      resizeTimeoutRef.current = null;
    }, 30);

    return () => {
      if (resizeTimeoutRef.current !== null) {
        clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = null;
      }
    };
  }, [
    canvasWidth,
    canvasHeight,
    // Добавляем resize функции в зависимости, чтобы при смене профиля (mobile/desktop/desktop2k)
    // вызывался resize с обновлённой fitAndPosition
    spaceHook.resizeSpace,
    backgroundHook.resizeBackground,
    stairsHook.resizeStairs,
    capitalistHook.resizeCapitalist,
    characterHook.resizeCharacter,
  ]);

  // cleanup
  useEffect(() => {
    return () => {
      const app = appRef.current;

      characterHook.destroyCharacter(app || undefined);
      capitalistHook.destroyCapitalist();
      stairsHook.destroyStairs();
      backgroundHook.destroyBackground();
      spaceHook.destroySpace();

      if (app) {
        try {
          app.ticker?.stop?.();
          if (app.stage) {
            app.stage.removeChildren();
          }
          app.renderer?.destroy?.(true);
          app.destroy(true);
        } catch (e) {
          console.warn("PioneerGame cleanup error:", e);
        }
        appRef.current = null;
      }

      pendingResizeRef.current = null;
      resizeTimeoutRef.current && clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = null;
    };
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
      }}
    >
      <div
        ref={containerRef}
        style={{ width: "100%", position: "relative", overflow: "hidden" }}
      >
        <InlineNotifications />

        <canvas
          ref={canvasRef}
          style={{
            width: "100%",
            height: `${canvasHeight}px`,
            display: "block",
          }}
        />
        <UserCashOutContainer />

        {isCountdown && (
          <div className={css.countdownContainer}>
            <p className={css.betText}>
              {t("main.place_your_bets").toUpperCase()}
            </p>

            {countdown ? (
              <div className={css.countdown}>
                <CountdownCircle
                  secondsLeft={countdown}
                  duration={countdownTotal ?? countdown} // TODO: по дефолту если оставить 6 и при заходе отнимать сразу от 6 duration
                />
              </div>
            ) : null}
          </div>
        )}
        {isCountdown ? null : (
          <div
            className={`${css.bigMultiplayer} ${gameState === "crashed" && css.crashed}`}
          >
            {multiplier.toFixed(2)}x
          </div>
        )}
      </div>

      {error && <div style={{ color: "#ff6b6b" }}>Ошибка: {error}</div>}
    </div>
  );
});
